import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import moment from 'moment';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import './MyFlightBookingDetails.css'; 

const MyFlightBookingDetails = () => {
    const { flightBookingId } = useParams(); 
    const { user } = useAuth();
    const [bookingDetails, setBookingDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const params = useParams();

    useEffect(() => {
        const getBookingDetails = async () => {
            if (!flightBookingId) {
                setError("Booking ID not found.");
                setLoading(false);
                return;
            }
            try {
                const response = await apiService.getAll(config.GETFLIGHTBOOKINGDETAILS + "/" + flightBookingId);
                console.log("Flight booking details response:", response);
                setBookingDetails(response);
            } catch (err) {
                setError("Failed to load booking details. Please try again.");
                console.error("Error fetching booking details:", err);
            } finally {
                setLoading(false);
            }
        };
        getBookingDetails();
    }, [flightBookingId]);

    const getTimeFromMins = (mins) => {
        if (!mins || mins < 0) return 'N/A';
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h}h ${m}m`;
    };

    const renderStatusBadge = (status) => {
        if (!status) return null;
        let badgeClass = 'bg-secondary';
        switch (status.toUpperCase()) {
            case 'CONFIRMED': badgeClass = 'bg-success'; break;
            case 'SUCCESS': badgeClass = 'bg-success'; break;
            case 'HOLD': badgeClass = 'bg-warning text-dark'; break;
            case 'PENDING': badgeClass = 'bg-info text-dark'; break;
            case 'CANCELLED': case 'FAILED': badgeClass = 'bg-danger'; break;
            default: break;
        }
        return <span className={`badge ${badgeClass}`}>{status}</span>;
    };

    if (loading) {
        return <div className="text-center p-5"><div className="spinner-border"></div><p className="mt-2">Loading Booking Details...</p></div>;
    }

    if (error) {
        return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
    }

    if (!bookingDetails) {
        return <div className="container mt-4"><p>No booking details found.</p></div>;
    }

    return (
        <div className="main-content">
            <div className="booking-details-wrapper">
                <div className="container">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="details-title mb-0">Flight Booking Details</h2>
                        <Link to="/my-flightbookings" className="btn btn-outline-secondary">
                            <i className="ri-arrow-left-line me-2"></i>Back to Bookings
                        </Link>
                    </div>
                    <div className="card common-details-card mb-4">
                        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-3">
                                <div>
                                    <span className="text-muted">Booking Status: </span>
                                    {renderStatusBadge(bookingDetails.bookingStatus)}
                                </div>
                                <div>
                                    <span className="text-muted">Payment Status: </span>
                                    {renderStatusBadge(bookingDetails.paymentStatus)}
                                </div>
                            </div>
                            <div className="d-flex gap-2 d-print-none">
                                <Link
                                    to={"/flight/voucher/" + params.flightBookingId}
                                    className="btn btn-sm btn-primary d-flex align-items-center shadow-none"
                                >
                                    <i className="ri-eye-line me-1"></i>
                                    <span>View Voucher</span>
                                </Link>
                                <Link
                                    to={"/flight/invoice/" + params.flightBookingId}
                                    className="btn btn-sm btn-secondary d-flex align-items-center shadow-none"
                                >
                                    <i className="ri-eye-line me-1"></i>
                                    <span>View Invoice</span>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Itinerary Section (Now only shows flight legs) */}
                    {bookingDetails.dcFlightLegs?.map((leg, index) => (
                        <div className="card itinerary-card mb-4" key={index}>
                            <div className="card-header bg-light">
                                <h5 className="mb-0">
                                    {leg.dcDisplayLegs?.[0]?.startSegment?.city} → {leg.dcDisplayLegs?.[0]?.endSegment?.city}
                                </h5>
                            </div>
                            <div className="card-body">
                                {leg.dcSegments?.map((segment, segIndex) => (
                                    <div className="segment-row" key={segIndex}>
                                        <div className="airline-info">
                                            <img src={`/assets/images/airlinelogos/${segment.tFAirlineData?.airlineCode}.png`} alt={segment.tFAirlineData?.airline} />
                                            <div>
                                                <strong>{segment.tFAirlineData?.airline}</strong>
                                                <small>{segment.tFAirlineData?.flightNumber}</small>
                                            </div>
                                        </div>
                                        <div className="departure-info">
                                            <strong>{moment(segment.tFDepartureData?.departureDateTime).format('HH:mm')}</strong>
                                            <small>{moment(segment.tFDepartureData?.departureDateTime).format('DD MMM, YYYY')}</small>
                                            <span>{segment.tFDepartureData?.city} ({segment.tFDepartureData?.airportCode})</span>
                                        </div>
                                        <div className="duration-info">
                                            <div className="line-with-plane">
                                                <i className="ri-plane-fill"></i>
                                            </div>
                                            <small>{getTimeFromMins(segment.tFAirlineData?.duration)}</small>
                                        </div>
                                        <div className="arrival-info">
                                            <strong>{moment(segment.tFArrivalData?.arrivalDateTime).format('HH:mm')}</strong>
                                            <small>{moment(segment.tFArrivalData?.arrivalDateTime).format('DD MMM, YYYY')}</small>
                                            <span>{segment.tFArrivalData?.city} ({segment.tFArrivalData?.airportCode})</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    <div className="row g-4">
                        {/* Traveler Details Column */}
                        <div className="col-lg-7">
                            <div className="card details-card">
                                <div className="card-header"><h6 className="mb-0">Traveler(s)</h6></div>
                                <div className="card-body">
                                    <ul className="list-group list-group-flush">
                                        {bookingDetails.travellerInfos?.map((t, i) => (
                                            <li key={i} className="list-group-item d-flex justify-content-between">
                                                <span><i className="ri-user-line me-2"></i>{t.title}. {t.firstName} {t.lastName}</span>
                                                <span className="badge bg-light text-dark">{t.travelerType}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Booking & Fare Summary Column */}
                        <div className="col-lg-5">
                            <div className="card details-card">
                                <div className="card-header"><h6 className="mb-0">Booking Summary</h6></div>
                                <div className="card-body">
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item d-flex justify-content-between">
                                            <span>Booking No:</span><strong>{bookingDetails.bookingNo}</strong>
                                        </li>
                                        <li className="list-group-item d-flex justify-content-between">
                                            <span>Airline PNR:</span><strong>{bookingDetails.pnr || 'N/A'}</strong>
                                        </li>
                                        <li className="list-group-item d-flex justify-content-between">
                                            <span>Booking Date:</span><strong>{moment(bookingDetails.bookingDate).format('DD MMM YYYY')}</strong>
                                        </li>
                                        <li className="list-group-item d-flex justify-content-between">
                                            <span>Total Fare:</span><strong className="text-primary fs-5">{bookingDetails.totalPrice} {bookingDetails.currency || 'INR'}</strong>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyFlightBookingDetails;